import { useDispatch } from 'react-redux';
import { useAuth0 } from 'src/auth/useAuth0.hook';
import store from 'src/redux/config/configureStore';
import {
  setAllCurrencyRates,
  setCurrencyRates,
  setUnauthorizedError,
} from 'src/redux/slices/general.slice';
import { UserRolesEnum } from 'src/types.d';
import { graphQLRequest } from 'src/utils/graphQL/graphQLRequestModule';
import { getAllCurrencyRatesQuery } from 'src/utils/graphQL/queries/general/getAllCurrencyRates';
import { getCurrencyRatesQuery } from 'src/utils/graphQL/queries/general/getCurrencyRates';
import { useGraphQLRequest } from 'src/utils/graphQL/useGraphQLRequest.hook';

//Dispatch only once
let dispatched = false;

/**
 * Flip the rates to be from the currency to USD
 * @param rates Rate before flipping
 * @returns Flipped rates
 */
export const flipRates = rates => {
  //validate rates
  if (!rates) return null;

  const flippedRates = {};
  Object.keys(rates).forEach(key => {
    //if key is not in the rates object, return 0
    if (!rates[key] || rates[key] <= 0) {
      flippedRates[key] = 0;
      return;
    }

    //if key doesn't start with ILS return the rate
    const shouldFlip: boolean = key.startsWith('ILS');

    //value exist, starts with ILS and not 0, flip the rate
    const value = shouldFlip ? 1 / rates[key] : rates[key];
    flippedRates[key] = value;
  });
  return flippedRates;
};

//Set the currency rates from the graph ql for the entire solution (both investors and operator)
export const useCurrencyConversionRate = () => {
  //read values from redux
  const { getTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const { client } = useGraphQLRequest();

  //dispatch the currency rates which triggers the loading
  async function getCurrencyRates() {
    const res = await getCurrencyRatesService();
    if (res?.err) {
      console.error('Error Encountered while trying to fetch currency rates, error: ', res.err);
      if (res.errorCode === 200) dispatch(setUnauthorizedError(true)); //trigger unauthorized error
    } else {
      dispatch(setCurrencyRates(res));
    }
  }

  //Read the currency conversion rates from the GraphQL DB
  async function getCurrencyRatesService() {
    const viewAsEmail = store.getState().investor?.viewAsEmail;
    const query = getCurrencyRatesQuery;
    const token = await getTokenSilently();
    const apiRes = await graphQLRequest(
      token,
      client,
      query,
      null,
      viewAsEmail ? UserRolesEnum.operator : UserRolesEnum.investor,
    );

    //flip the rates
    const flippedRates = flipRates(apiRes?.currencyRates);

    //return the flipped rates if exists, otherwise return the original rates
    return flippedRates ?? apiRes;
  }

  //dispatch the currency rates which triggers the loading
  async function getAllCurrencyRates() {
    const res = await getAllCurrencyRatesService();
    if (res?.err) {
      console.error('Error Encountered while trying to fetch all currency rates, error: ', res.err); //todo nir should tostify it?
    } else {
      //flip the rates and store in redux
      const flippedRates = res?.allCurrencyRates?.map(rate => {
        return {
          ...rate,
          currencyRates: flipRates(rate.currencyRates),
        };
      });
      dispatch(setAllCurrencyRates(flippedRates));
    }
  }

  //Read the All currency conversion rates from all stored dates from the GraphQL DB
  async function getAllCurrencyRatesService() {
    const viewAsEmail = store.getState().investor?.viewAsEmail;
    const query = getAllCurrencyRatesQuery;
    const token = await getTokenSilently();
    const apiRes = await graphQLRequest(
      token,
      client,
      query,
      null,
      viewAsEmail ? UserRolesEnum.operator : UserRolesEnum.investor,
    );

    //flip the rates
    const flippedRates = flipRates(apiRes?.currencyRates);

    //return the flipped rates if exists, otherwise return the original rates
    return flippedRates ?? apiRes;
  }

  //call get currency rates once
  if (!dispatched) {
    dispatched = true;
    getCurrencyRates();
    getAllCurrencyRates();
  }
};
